import {  ActionResponse, ActionStatusHelper, AppEnum, DOMAIN_TYPES, ResponseHook, stringCompare, useAppEnum } from "@/Shared";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAccessRight } from "@/Domains/Auth";
import { usePermission } from "@/OneLiners";

/**
 * =====================================================
 * Hook: useDocumentCategory
 * =====================================================
 *
 * Description:
 * Custom hook to manage the current document category and type based on:
 * - URL parameters (category, type)
 * - Modal type override
 * - User access rights
 *
 * Responsibilities:
 * - Computes the current category and type for a document modal or page.
 * - Filters available document categories according to user permissions.
 * - Provides sorted and filtered document categories for UI consumption.
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export const useDocumentCategory = (modalType?: string) => {


        /** =====================================================
         * Permissions & Access Rights
         * =====================================================
         *
         * Fetches raw permissions and computes access rights
         * for the document domain.
         */
    const {
        dataMap: permissionMap,
        stateResponse: permissionStateResponse,
    } = usePermission();

    /**
         * Access rights computed from the permission list.
         * This object centralizes authorization logic for the Contact domain.
         */
    const accessRight = useAccessRight(permissionMap);


    /** =====================================================
     * URL Params & State Initialization
     * =====================================================
     *
     * Extract category/type from URL and manage internal state.
     */
    const { category, type } = useParams();

    const [curCategory, setCurCategory] = useState<string | undefined>();
    const [curType, setCurType] = useState<string | undefined>(modalType);
    const [response, setResponse] = useState<ActionResponse>({status : 'idle'});

    // List of document categories filtered for the current user
    const [docCategoryList, setDocCategoryList] = useState<AppEnum[]>([]);
    const { DocumentEnums } = useAppEnum();

    // Map defining which document categories the current user can access
    const DOCUMENT_CATEGORY_ACCESS_MAP: Record<string, boolean> =   {
        Tool: true,
        Mandate: accessRight.canExecute(DOMAIN_TYPES.Mandate),
        Property: accessRight.canExecute(DOMAIN_TYPES.Property),
        Lease: accessRight.canExecute(DOMAIN_TYPES.Lease),
    };

    /** =====================================================
     * Effect: Update current category/type from URL or modal
     * =====================================================
     *
     * Computes the effective current category and type whenever
     * modalType or URL parameters change.
     */
    useEffect(() => {
        ResponseHook.loading(setResponse);
        const { curCategory, curType } = computeCategoryAndType(modalType, category, type);
        setCurCategory(String(curCategory));
        setCurType(String(curType));
    }, [modalType, type, category]);


    /** =====================================================
     * Effect: Update category list when response succeeds or category changes
     * =====================================================
     *
     * Filters and sorts document categories for the user after
     * data is successfully loaded or the category changes.
     */
    useEffect(() => {
        if (ActionStatusHelper.isSucceeded(permissionStateResponse.status)) {
            updateCategory();
            ResponseHook.success(setResponse);
        }
    }, [curCategory, permissionStateResponse.status]);


    /** =====================================================
     * Helper: Update filtered and sorted document categories
     * =====================================================
     *
     * Filters categories based on access rights and current selection.
     * Sorts categories alphabetically by label.
     */
    function updateCategory() {
        
        const canshowCategory = (cat: AppEnum) =>
            DOCUMENT_CATEGORY_ACCESS_MAP[cat.value] &&
            (!curCategory || cat.label === curCategory || cat.value === curCategory);

        const sortCategory = (cat1: AppEnum, cat2: AppEnum) => stringCompare(cat1.label, cat2.label);
        const categoryList = DocumentEnums.DocumentCategoryEnum.filter(canshowCategory).sort(sortCategory);
        setDocCategoryList(categoryList);
    }

    /** =====================================================
     * Helper: Determine current category and type from modal or URL
     * =====================================================
     *
     * Resolves category/type either from modalType override or
     * URL parameters, with proper decoding.
     */
    function computeCategoryAndType(modalType?: string, category?: string, type?: string) {
        if (modalType) {
            const matchedCategory = DocumentEnums.DocumentCategoryEnum.find(cat => cat.children?.some(t => t.value === modalType))?.value;
            return { curCategory: matchedCategory, curType: modalType };
        }

        const categoryLabel = category ? decodeURIComponent(category) : undefined;
        const foundCategory = DocumentEnums.DocumentCategoryEnum.find(cat => cat.label === categoryLabel)?.value;
        const foundType = DocumentEnums.DocumentTypeEnum.find(t => t.label === decodeURIComponent(type ?? ""))?.value;

        return { curCategory: foundCategory, curType: foundType };
    }
     /** =====================================================
     * Return API
     * =====================================================
     *
     * Exposes state and setters for use in consuming components.
     */
    return {
        docCategoryList,
        setCurCategory,
        curCategory,
        curType,
        accessRight,
        categoryResponse : response
    }
}